import React, { useEffect, useRef } from 'react';
import { Smartphone } from 'lucide-react';
import AppImage from '@/core/presentation/components/ui/AppImage';
import { LANDING_HERO_TRUST_PWA_LABEL } from '@/core/presentation/constants/landing-pwa-promo-copy';

const HeroSection: React.FC = () => {
  const headlineRef = useRef<HTMLHeadingElement>(null);
  const subRef = useRef<HTMLParagraphElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const card1Ref = useRef<HTMLDivElement>(null);
  const card2Ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const elements = [
      { el: headlineRef.current, delay: 0 },
      { el: subRef.current, delay: 150 },
      { el: ctaRef.current, delay: 300 },
      { el: card1Ref.current, delay: 500 },
      { el: card2Ref.current, delay: 650 },
    ];

    elements.forEach(({ el, delay }) => {
      if (!el) return;
      el.style.opacity = '0';
      el.style.transform = 'translateY(30px)';
      setTimeout(() => {
        if (el) {
          el.style.transition = 'opacity 0.9s cubic-bezier(0.16, 1, 0.3, 1), transform 0.9s cubic-bezier(0.16, 1, 0.3, 1)';
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
        }
      }, delay + 200);
    });
  }, []);

  return (
    <header
      className="relative min-h-screen flex flex-col justify-center overflow-hidden animated-gradient-bg"
      style={{ paddingTop: '80px' }}>

      {/* Background decorative blobs */}
      <div
        className="absolute top-20 right-10 w-96 h-96 blob-decoration pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(126,207,239,0.12) 0%, transparent 70%)',
          filter: 'blur(40px)'
        }} />
      <div
        className="absolute bottom-20 left-0 w-80 h-80 blob-decoration pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(74,184,224,0.1) 0%, transparent 70%)',
          filter: 'blur(50px)',
          animationDelay: '3s'
        }} />

      {/* Background: imagem + overlay azul em camadas — mistura sutil (stacking explícito para Safari) */}
      <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 0 }}>
        <div className="absolute inset-0" style={{ zIndex: 0 }}>
          <AppImage
            src="https://img.rocket.new/generatedImages/rocket_gen_img_1415cd390-1767990311262.png"
            alt="Técnico de ar-condicionado realizando manutenção em unidade externa"
            fill
            className="object-cover opacity-[0.35]"
            priority
          />
        </div>
        {/* Overlay azul semi-transparente: deixa a imagem aparecer por baixo, mantendo o tom navy */}
        <div
          className="absolute inset-0 bg-navy-dark/50"
          style={{ zIndex: 1 }}
        />
        <div
          className="absolute inset-0 bg-gradient-to-b from-navy-dark/50 via-navy-dark/30 to-navy-dark/70"
          style={{ zIndex: 2 }}
        />
      </div>

      {/* Grid lines decoration */}
      <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 0 }}>
        <div className="w-full h-full" style={{
          backgroundImage: `linear-gradient(rgba(126,207,239,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(126,207,239,0.04) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />
      </div>

      <div className="relative z-10 max-w-[1320px] mx-auto px-6 w-full py-16">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* Left content */}
          <div className="lg:col-span-7">
            {/* Tag */}
            <div className="mb-6">
              <span className="tag-badge bg-cyan/15 text-cyan border border-cyan/30">
                Lembretes de manutenção no seu controle
              </span>
            </div>

            {/* Headline */}
            <h1
              ref={headlineRef}
              className="font-fraunces text-white leading-[1.05] tracking-tight mb-6"
              style={{ fontSize: 'clamp(2.6rem, 6vw, 5rem)' }}>
              Você está esquecendo
              <br />
              <span className="italic gradient-text">clientes antigos</span>
              <br />
              <span className="text-white/90">e perdendo receita todo mês.</span>
            </h1>

            <p
              ref={subRef}
              className="text-white/65 text-lg md:text-xl font-light leading-relaxed mb-10 max-w-lg">
              O <strong className="text-white font-semibold">RetornaAr</strong> avisa <em>você</em> — o dono — quando é hora de entrar em contato com um cliente para a próxima manutenção. Você mantém o controle total da comunicação.
            </p>

            <div ref={ctaRef} className="flex w-full flex-row sm:w-auto">
              <a
                href="#como-funciona"
                className="btn-primary flex w-full items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-bold glow-pulse sm:w-fit sm:shrink-0 sm:gap-3 sm:px-8 sm:py-4 sm:text-base"
                aria-label="Ir para a seção Como funciona">
                Ver como funciona
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden>
                  <path d="M12 5v14M5 12l7 7 7-7" />
                </svg>
              </a>
            </div>

            {/* Trust signals */}
            <div className="mt-10 flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-2 text-white/50 text-sm">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-cyan">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
                Você no controle
              </div>
              <div className="flex items-center gap-2 text-white/50 text-sm">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-cyan">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
                Lembrete via WhatsApp
              </div>
              <div className="flex items-center gap-2 text-white/50 text-sm">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-cyan">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
                Cadastro em menos de 1 minuto
              </div>
              <div className="flex items-center gap-2 text-white/50 text-sm">
                <Smartphone className="h-4 w-4 shrink-0 text-cyan" aria-hidden />
                {LANDING_HERO_TRUST_PWA_LABEL}
              </div>
            </div>
          </div>

          {/* Right: Floating cards */}
          <div className="lg:col-span-5 relative hidden lg:flex flex-col items-center justify-center" style={{ minHeight: '420px' }}>
            {/* Main card */}
            <div
              ref={card1Ref}
              className="float-card glass-panel rounded-2xl p-6 w-full max-w-sm shadow-2xl">
              <div className="flex items-start justify-between mb-5">
                <div>
                  <p className="text-white/50 text-xs font-semibold uppercase tracking-widest mb-1">Lembrete do Sistema</p>
                  <p className="font-fraunces text-white text-xl font-medium leading-snug">Hora de ligar para o cliente</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-cyan/20 flex items-center justify-center">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#7ECFEF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.63A2 2 0 012 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 7.91a16 16 0 006.18 6.18l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
                  </svg>
                </div>
              </div>
              <div className="p-4 rounded-xl bg-white/5 border border-white/10 mb-4">
                <p className="text-white text-sm font-medium mb-1">📱 Mensagem no seu WhatsApp:</p>
                <p className="text-white/70 text-sm leading-relaxed italic">
                  "RetornaAr: Hora de entrar em contato com <strong className='text-cyan'>João Silva</strong> — manutenção do AC vence hoje."
                </p>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-white/50">Cliente</span>
                  <span className="text-white font-semibold">João Silva</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-white/50">Último serviço</span>
                  <span className="text-white font-semibold">6 meses atrás</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-white/50">Periodicidade</span>
                  <span className="text-cyan font-semibold">6 meses</span>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-white/10">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                  <span className="text-green-400 text-xs font-medium">Lembrete enviado para você agora</span>
                </div>
              </div>
            </div>

            {/* Secondary card */}
            <div
              ref={card2Ref}
              className="float-card-delayed glass-panel rounded-2xl p-5 absolute -bottom-4 -right-6 w-64 shadow-xl"
              style={{ zIndex: 10 }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                </div>
                <div>
                  <p className="text-white text-sm font-semibold">Serviço registrado!</p>
                  <p className="text-white/40 text-xs">Próximo lembrete calculado</p>
                </div>
              </div>
              <p className="text-white/65 text-xs leading-relaxed">
                Próxima manutenção de <strong className="text-white">Maria Costa</strong> em <span className="text-cyan">12 meses</span>.
              </p>
              <div className="mt-2 flex items-center gap-1">
                <div className="w-1.5 h-1.5 rounded-full bg-cyan" />
                <span className="text-cyan text-xs">Você será avisado na hora certa</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/30 z-10">
        <span className="text-xs font-medium tracking-widest uppercase">Role para baixo</span>
        <div className="w-px h-12 bg-gradient-to-b from-white/30 to-transparent" />
      </div>
    </header>
  );
};

export default HeroSection;